import React, { useEffect, useState, useCallback } from 'react';
import {
  Search, ExternalLink, MessageCircle, Globe, Code2, Boxes,
  Image as ImageIcon, Link2, Layers, Sparkles, ChevronRight, Calendar,
  Plus, Zap, GitBranch, ArrowRight, X,
} from 'lucide-react';
import ProjectDetailView from './ProjectDetailView';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KIND_META = {
  build:   { label: 'Built',    color: '#7C3AED', icon: Boxes },
  import:  { label: 'Imported', color: '#A855F7', icon: Code2 },
  scrape:  { label: 'Scraped',  color: '#06B6D4', icon: Globe },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

export default function ProjectsView({ onOpenInBuild, onRescrape, onStartFreshBuild, onOpenImportFlow }) {
  const [data, setData] = useState({ items: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | build | import | scrape
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // selected item for detail view
  const [showNewModal, setShowNewModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/projects/all`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setData(d);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return (
      <ProjectDetailView
        item={selected}
        onBack={() => setSelected(null)}
        onOpenInBuild={onOpenInBuild}
        onRescrape={onRescrape}
      />
    );
  }

  const items = (data.items || []).filter(it => {
    if (filter !== 'all' && it.kind !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      return (it.title || '').toLowerCase().includes(q)
          || (it.subtitle || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div data-testid="projects-view" style={{ height: '100%', overflowY: 'auto', color: '#0F0F1A' }}>
      <div style={{ padding: '32px 40px', maxWidth: 1400, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          marginBottom: 28, display: 'flex', alignItems: 'flex-end',
          justifyContent: 'space-between', gap: 20, flexWrap: 'wrap',
        }}>
          <div>
            <h2 style={{ fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>
              Workspace
            </h2>
            <p style={{ fontSize: 13, color: 'rgba(15,15,30,0.55)', marginTop: 6 }}>
              Every project, repository, and scrape — all in one timeline.
            </p>
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            data-testid="projects-new-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 18px',
              background: 'linear-gradient(135deg, #7C3AED 0%, #00A88C 100%)',
              color: 'var(--studio-bg)', border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 800,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              boxShadow: '0 0 24px rgba(124,58,237,0.35)',
              transition: 'transform 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Plus size={13} /> New project
          </button>
        </div>

        {/* Stats strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          <StatTile label="All assets" value={data.totals?.all || 0} accent="#fff" />
          <StatTile label="Builds" value={data.totals?.build || 0} accent="#7C3AED" />
          <StatTile label="Repos" value={data.totals?.import || 0} accent="#A855F7" />
          <StatTile label="Scrapes" value={data.totals?.scrape || 0} accent="#06B6D4" />
        </div>

        {/* Filters + search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ display: 'flex', border: '1px solid rgba(15,15,30,0.08)' }}>
            {[
              { k: 'all', label: 'All' },
              { k: 'build', label: 'Builds' },
              { k: 'import', label: 'Repositories' },
              { k: 'scrape', label: 'Scrapes' },
            ].map(t => (
              <button
                key={t.k}
                onClick={() => setFilter(t.k)}
                data-testid={`projects-filter-${t.k}`}
                style={{
                  padding: '8px 14px',
                  background: filter === t.k ? 'rgba(124,58,237,0.1)' : 'transparent',
                  border: 'none',
                  color: filter === t.k ? '#7C3AED' : 'rgba(15,15,30,0.65)',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 14px', background: 'rgba(15,15,30,0.035)',
            border: '1px solid rgba(15,15,30,0.08)',
          }}>
            <Search size={13} color="rgba(15,15,30,0.48)" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, repo, URL..."
              data-testid="projects-search"
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#0F0F1A', fontSize: 12, fontFamily: 'inherit' }}
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12 }}>
            Loading workspace...
          </div>
        ) : items.length === 0 ? (
          <EmptyState filter={filter} onNew={() => setShowNewModal(true)} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => (
              <Row key={`${item.kind}:${item.id}`} item={item} onClick={() => setSelected(item)} />
            ))}
          </div>
        )}
      </div>

      {showNewModal && (
        <NewProjectModal
          onClose={() => setShowNewModal(false)}
          onPickBuild={() => { setShowNewModal(false); onStartFreshBuild?.(); }}
          onPickImport={() => { setShowNewModal(false); onOpenImportFlow?.(); }}
        />
      )}
    </div>
  );
}

/* ------------ Pieces ------------ */
function StatTile({ label, value, accent }) {
  return (
    <div style={{
      padding: 18, background: 'rgba(15,15,30,0.025)',
      border: '1px solid rgba(15,15,30,0.06)',
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
        color: 'rgba(15,15,30,0.48)', textTransform: 'uppercase',
      }}>{label}</div>
      <div style={{
        fontSize: 28, fontWeight: 800, color: accent, marginTop: 6,
        fontFamily: "'JetBrains Mono', monospace",
      }}>{value}</div>
    </div>
  );
}

function Row({ item, onClick }) {
  const meta = KIND_META[item.kind] || KIND_META.build;
  const Icon = meta.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`workspace-row-${item.id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 16,
        width: '100%', padding: '14px 18px', textAlign: 'left',
        background: 'rgba(15,15,30,0.025)',
        border: '1px solid rgba(15,15,30,0.06)',
        cursor: 'pointer', fontFamily: 'inherit', color: '#0F0F1A',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${meta.color}40`; e.currentTarget.style.background = `${meta.color}06`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,15,30,0.06)'; e.currentTarget.style.background = 'rgba(15,15,30,0.025)'; }}
    >
      <div style={{
        width: 38, height: 38, flexShrink: 0,
        background: `${meta.color}18`, border: `1px solid ${meta.color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={16} color={meta.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
            color: meta.color, textTransform: 'uppercase',
            padding: '2px 6px', background: `${meta.color}10`,
            border: `1px solid ${meta.color}30`,
          }}>{meta.label}</span>
          <span style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.title}
          </span>
        </div>
        <div style={{
          fontSize: 11, color: 'rgba(15,15,30,0.55)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          maxWidth: 600,
        }}>
          {item.subtitle || '—'}
        </div>
      </div>
      {item.kind === 'scrape' && (
        <Badges counts={[
          { icon: ImageIcon, val: item.counts?.images, color: '#06B6D4' },
          { icon: Link2,     val: item.counts?.links,  color: '#22D3EE' },
          { icon: Layers,    val: item.counts?.headings, color: '#A855F7' },
        ]} />
      )}
      {(item.kind === 'build' || item.kind === 'import') && (
        <Badges counts={[
          { icon: MessageCircle, val: item.chats_count, color: '#7C3AED' },
        ]} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'rgba(15,15,30,0.48)', flexShrink: 0 }}>
        <Calendar size={11} /> {fmtDate(item.date)}
      </div>
      <ChevronRight size={14} color="rgba(15,15,30,0.4)" />
    </button>
  );
}

function Badges({ counts }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
      {counts.map((b, i) => {
        const I = b.icon;
        return (
          <span key={i} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 10, color: b.color,
            padding: '3px 7px',
            background: `${b.color}10`,
            border: `1px solid ${b.color}25`,
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 600,
          }}>
            <I size={9} /> {b.val ?? 0}
          </span>
        );
      })}
    </div>
  );
}

function EmptyState({ filter, onNew }) {
  return (
    <div style={{
      padding: 60, textAlign: 'center',
      background: 'rgba(15,15,30,0.025)',
      border: '1px dashed rgba(15,15,30,0.1)',
    }}>
      <Sparkles size={28} color="#7C3AED" style={{ margin: '0 auto 12px', display: 'block', opacity: 0.5 }} />
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
        Nothing here yet
      </div>
      <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.48)', marginBottom: 18 }}>
        {filter === 'scrape' ? 'Run a Web Intelligence scrape from the Build view.'
          : filter === 'import' ? 'Import a repo to consult on it with AI.'
          : 'Build a new project or import one to get started.'}
      </div>
      {filter !== 'scrape' && (
        <button
          onClick={onNew}
          data-testid="empty-new-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px',
            background: 'rgba(124,58,237,0.12)', color: '#7C3AED',
            border: '1px solid rgba(124,58,237,0.4)',
            cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
          }}
        >
          <Plus size={11} /> New project
        </button>
      )}
    </div>
  );
}

/* ============== New project modal — choice between Build and Import ============== */
function NewProjectModal({ onClose, onPickBuild, onPickImport }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)',
        backdropFilter: 'blur(10px)', zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, animation: 'fadeIn 0.18s ease-out',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        data-testid="new-project-modal"
        style={{
          width: '100%', maxWidth: 720,
          background: '#0F0F1A',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: 28,
          boxShadow: '0 30px 90px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 22 }}>
          <div style={{
            width: 38, height: 38, flexShrink: 0,
            background: 'rgba(124,58,237,0.16)', border: '1px solid rgba(124,58,237,0.45)',
            color: '#7C3AED', borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Sparkles size={17} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: '#7C3AED',
            }}>
              New venture
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#F5F5F7', marginTop: 4, letterSpacing: '-0.01em' }}>
              How do you want to start?
            </div>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)', marginTop: 6, lineHeight: 1.55 }}>
              Pick a path. You can always promote any project to <strong style={{ color: '#FFD700' }}>Monetize</strong> later.
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="new-project-modal-close"
            style={{
              width: 28, height: 28, flexShrink: 0,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.65)',
              borderRadius: 8,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={13} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <PathCard
            testid="new-project-build"
            color="#7C3AED"
            Icon={Zap}
            tag="Vibe-coding"
            title="Build from scratch"
            description="Start with a blank canvas. Describe what you want, the AI builder ships it."
            features={[
              'AI-assisted full-stack scaffolding',
              'Live preview as you iterate',
              'Saved automatically as a Build project',
            ]}
            cta="Open the builder"
            onClick={onPickBuild}
          />
          <PathCard
            testid="new-project-import"
            color="#A855F7"
            Icon={GitBranch}
            tag="Existing code"
            title="Import a repository"
            description="Connect GitHub, pick a repo. We'll clone it and pair it with an AI consultant."
            features={[
              'Side-by-side preview + chat',
              'Multimodal vision on your build',
              'Saved as a Repository project',
            ]}
            cta="Connect GitHub"
            onClick={onPickImport}
          />
        </div>
      </div>
    </div>
  );
}

function PathCard({ testid, color, Icon, tag, title, description, features, cta, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        position: 'relative', overflow: 'hidden',
        padding: 22, textAlign: 'left',
        background: 'rgba(255,255,255,0.025)',
        border: `1px solid ${color}50`,
        borderRadius: 12,
        color: '#F5F5F7', fontFamily: 'inherit', cursor: 'pointer',
        transition: 'all 0.18s',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = `${color}10`;
        e.currentTarget.style.borderColor = `${color}90`;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 12px 36px ${color}30`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
        e.currentTarget.style.borderColor = `${color}50`;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{
        position: 'absolute', top: 0, right: 0, width: 140, height: 140,
        background: `radial-gradient(circle at top right, ${color}22, transparent 65%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: `${color}22`, color, border: `1px solid ${color}55`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={16} />
        </div>
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
          color, padding: '3px 8px', borderRadius: 4,
          background: `${color}18`, border: `1px solid ${color}40`,
        }}>{tag}</span>
      </div>

      <div style={{ position: 'relative' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#F5F5F7', marginBottom: 6, letterSpacing: '-0.01em' }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.62)', lineHeight: 1.55 }}>
          {description}
        </div>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
        {features.map((f, i) => (
          <li key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            fontSize: 11.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.5,
          }}>
            <span style={{
              width: 4, height: 4, marginTop: 6, flexShrink: 0,
              background: color, borderRadius: '50%',
            }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div style={{
        marginTop: 'auto', paddingTop: 10,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
        color, position: 'relative',
      }}>
        {cta} <ArrowRight size={11} />
      </div>
    </button>
  );
}
